import { useEffect, useId, useState } from 'react'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import { Button } from '@cannect/new-components/atoms'
import { useUploadSubscriptionDocument } from '@cannect/services/resources/recurrency/hooks'
import { getRecurrencyError } from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDocumentType } from '@cannect/services/resources/recurrency/requests'
import type { SubscriptionDetail } from '@cannect/services/resources/recurrency/types'
import { subscriptionsCopy } from '../copy'
import { DateField } from './DateField'
import { DialogShell } from './DialogShell'

const copy = subscriptionsCopy.upload
export const UPLOAD_MAX_BYTES = 2 * 1024 * 1024
export const UPLOAD_ACCEPT = ['.pdf', '.jpg', '.jpeg', '.png']

export const validateUploadFile = (file: File) => {
  const name = file.name.toLowerCase()
  if (!UPLOAD_ACCEPT.some((ext) => name.endsWith(ext))) return copy.badFormat
  if (file.size > UPLOAD_MAX_BYTES) return copy.tooBig
  return null
}

type Props = {
  detail: SubscriptionDetail
  type: SubscriptionDocumentType
  open: boolean
  onOpenChange: (open: boolean) => void
  onUploaded: (type: SubscriptionDocumentType) => void
}

export const UploadDocumentDialog = ({ detail, type, open, onOpenChange, onUploaded }: Props) => {
  const fileId = useId()
  const [file, setFile] = useState<File | null>(null)
  const [emission, setEmission] = useState('')
  const [fileError, setFileError] = useState<string | null>(null)
  const [emissionError, setEmissionError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const upload = useUploadSubscriptionDocument(detail.id)
  const today = format(new Date(), 'yyyy-MM-dd')

  useEffect(() => {
    if (open) return
    setFile(null)
    setEmission('')
    setFileError(null)
    setEmissionError(null)
    setSubmitError(null)
  }, [open])

  const submit = async () => {
    setSubmitError(null)
    if (!file) {
      setFileError(copy.fileRequired)
      return
    }
    if (type === 'anvisa') {
      if (!emission) {
        setEmissionError(copy.emissionRequired)
        return
      }
      if (emission > today) {
        setEmissionError(copy.emissionFuture)
        return
      }
    }
    try {
      await upload.mutateAsync({ subscription: detail, type, file, emission: type === 'anvisa' ? emission : undefined })
      toast.success(copy.success)
      onUploaded(type)
      onOpenChange(false)
    } catch (err) {
      setSubmitError(getRecurrencyError(err).message)
    }
  }

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={type === 'prescription' ? copy.titlePrescription : copy.titleAnvisa}
      description={copy.description}
      footer={
        <>
          <Button variant="outline_light" onClick={() => onOpenChange(false)}>
            {subscriptionsCopy.buttons.back}
          </Button>
          <Button isLoading={upload.isPending} onClick={submit}>
            {copy.submit}
          </Button>
        </>
      }>
      <div className="flex flex-col gap-1">
        <label htmlFor={fileId} className="text-sm font-semibold text-neutral-800">
          {copy.chooseFile}
        </label>
        <input
          id={fileId}
          type="file"
          accept={UPLOAD_ACCEPT.join(',')}
          aria-invalid={Boolean(fileError)}
          onChange={(event) => {
            const next = event.target.files?.[0] || null
            const error = next ? validateUploadFile(next) : null
            setFileError(error)
            setFile(error ? null : next)
          }}
          className="text-sm"
        />
        <span aria-live="polite" className="min-h-[1rem] text-xs text-red-700">
          {fileError}
        </span>
      </div>

      {type === 'anvisa' && (
        <DateField
          label={copy.emission}
          max={today}
          value={emission}
          error={emissionError || undefined}
          onChange={(event) => {
            setEmission(event.target.value)
            setEmissionError(null)
          }}
        />
      )}

      {submitError && (
        <p role="alert" className="m-0 text-sm text-red-700">
          {submitError}
        </p>
      )}
    </DialogShell>
  )
}
